import { Alert, Button, Stack, Typography, Link } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { HealthError } from "@/hooks/useNativeHealthSDK";

interface HealthErrorBannerProps {
  error: HealthError | null;
  isSamsungDevice: boolean;
  onRetry: () => void;
  onOpenSettings: () => void;
  onShowSamsungGuidance: () => void;
}

const ERROR_CODE_TO_I18N_KEY: Record<string, string> = {
  ERR_HEALTH_CONNECT: "health.errors.healthConnectUnavailable",
  ERR_HEALTH_CONNECT_UNAVAILABLE: "health.errors.healthConnectUnavailable",
  ERR_HEALTH_CONNECT_NEEDS_UPDATE: "health.errors.healthConnectNeedsUpdate",
  ERR_PERMISSION_DENIED: "health.errors.permissionDenied",
  ERR_PERMISSION_DENIED_PERMANENT: "health.errors.permissionDeniedPermanent",
  ERR_MANUAL_PERMISSION_REQUIRED: "health.errors.manualPermissionRequired",
  ERR_NO_DATA: "health.errors.noData",
  ERR_SYNC_FAILED: "health.errors.syncFailed",
  ERR_RATE_LIMITED: "health.errors.rateLimited",
  ERR_NATIVE_MODULE_NOT_FOUND: "health.errors.nativeModuleNotFound",
  ERR_PLATFORM_UNAVAILABLE: "health.errors.platformUnavailable",
};

/**
 * Maps a HealthError to user-friendly translated messages with actionable buttons.
 */
export default function HealthErrorBanner({
  error,
  isSamsungDevice,
  onRetry,
  onOpenSettings,
  onShowSamsungGuidance,
}: HealthErrorBannerProps) {
  const { t } = useTranslation();

  if (!error) {
    return null;
  }

  const i18nKey = ERROR_CODE_TO_I18N_KEY[error.code];
  const message = String(i18nKey ? t(i18nKey as any) : error.message);

  const severity =
    error.code === "ERR_NO_DATA" || error.code === "ERR_RATE_LIMITED"
      ? "info"
      : "warning";

  return (
    <Alert
      severity={severity}
      sx={{ mb: 2, alignItems: "flex-start" }}
      data-testid="health-error-banner"
    >
      <Stack spacing={1}>
        <Typography variant="body2">{message}</Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          {error.action === "open_settings" && (
            <Button
              size="small"
              variant="outlined"
              onClick={onOpenSettings}
              data-testid="health-error-open-settings"
            >
              {error.code === "ERR_HEALTH_CONNECT" ||
              error.code === "ERR_HEALTH_CONNECT_UNAVAILABLE"
                ? t("health.permissions.openHealthConnect")
                : t("health.permissions.openSettings")}
            </Button>
          )}
          {error.action === "reauthenticate" && (
            <Button
              size="small"
              variant="outlined"
              onClick={onOpenSettings}
              data-testid="health-error-reauthenticate"
            >
              {t("health.permissions.openSettings")}
            </Button>
          )}
          {error.action !== "open_settings" &&
            error.action !== "reauthenticate" && (
              <Button
                size="small"
                variant="outlined"
                onClick={onRetry}
                data-testid="health-error-retry"
              >
                {t("health.permissions.tryAgain")}
              </Button>
            )}
        </Stack>

        {isSamsungDevice && (
          <Link
            component="button"
            variant="body2"
            onClick={onShowSamsungGuidance}
            sx={{ alignSelf: "flex-start" }}
            data-testid="health-error-samsung-link"
          >
            {t("health.samsung.guidanceTitle")}
          </Link>
        )}
      </Stack>
    </Alert>
  );
}
